import React, { useState } from 'react'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { GET, POST, httpOk } from 'helpers/api'

/**
 * Сервисные операции: рассылка обновления на кассы, проверка сокет-каналов
 * и привязка продуктов к складу.
 *
 * В привязке раньше были зашиты конкретные логины и id склада — утилита
 * работала ровно один раз для одного клиента. Теперь и продукты, и склад
 * выбираются на форме, причём разными логинами: у сети блюда часто заведены
 * на головном бренде, а склад — у филиала.
 */
function ServiceTab({ users }) {
	const { t } = useTranslation()

	const [fromUser, setFromUser] = useState(null)
	const [toUser, setToUser] = useState(null)
	const [poses, setPoses] = useState([])
	const [warehouses, setWarehouses] = useState([])
	const [selectedPos, setSelectedPos] = useState(null)
	const [selectedWms, setSelectedWms] = useState(null)
	const [result, setResult] = useState(null)

	async function sendUpdate() {
		await GET('/admin/pos-update-all-version', {}, { loader: true })
	}

	// Обновление одной кассе: выбор логина → точки → команда на канал pos.{id}
	const [updateUser, setUpdateUser] = useState(null)
	const [updatePoses, setUpdatePoses] = useState([])
	const [updatePos, setUpdatePos] = useState(null)

	async function handleSelectUpdateUser(option) {
		setUpdateUser(option)
		setUpdatePos(null)
		setUpdatePoses([])
		if (!option?.login) return
		const response = await GET('/admin/sql/poses', { 'login': option.login }, { loader: true })
		if (httpOk(response)) setUpdatePoses(response.data)
	}

	async function sendPosUpdate() {
		if (!updatePos) {
			toast.error('Выберите торговую точку')
			return
		}
		await POST('/admin/pos-update-version', { 'pos_id': Number(updatePos.id) }, { loader: true })
	}

	async function checkSocket(type) {
		await GET(`/admin/pos-check-socket?type=${type}`, {}, { loader: true })
	}

	async function getPoses(login) {
		const response = await GET('/admin/sql/poses', { 'login': login }, { loader: true })
		if (httpOk(response)) setPoses(response.data)
	}

	async function getWarehouses(login, posId) {
		const response = await GET('/admin/duplicate/warehouses', {
			'login': login,
			'pos_id': posId,
		}, { loader: true })
		if (httpOk(response)) setWarehouses(response.data)
	}

	function handleSelectToUser(option) {
		setToUser(option)
		setSelectedPos(null)
		setSelectedWms(null)
		setPoses([])
		setWarehouses([])
		setResult(null)
		if (option?.login) getPoses(option.login)
	}

	function handleSelectPos(option) {
		setSelectedPos(option)
		setSelectedWms(null)
		setWarehouses([])
		setResult(null)
		if (option?.id) getWarehouses(toUser.login, option.id)
	}

	async function linkProductWms() {
		if (!fromUser || !toUser || !selectedPos || !selectedWms) {
			toast.error(t('admin_tools_product_wms_required'))
			return
		}

		setResult(null)
		const response = await POST('/admin/product-wms', {
			'from_login': fromUser.login,
			'to_login': toUser.login,
			'pos_id': Number(selectedPos.id),
			'wms_id': Number(selectedWms.id),
		}, { loader: true, notification: false })

		if (httpOk(response)) {
			setResult(response.data)
			toast.success(response.data?.message || t('success'))
		}
	}

	return (
		<div className="row">
			<div className="col-md-5">
				<div className="card">
					<div className="card-body">
						<div className="tool-section-title">
							{t('admin_tools_pos_channels')}
						</div>
						<div className="text-muted fz-12 mb-3">
							{t('admin_tools_socket_hint')}
						</div>

						<button type="button" className="btn btn-primary w-100 mb-2"
							onClick={() => sendUpdate()}>
							<i className="uil uil-sync me-1"></i>
							{t('admin_tools_pos_update')}
						</button>
						<button type="button" className="btn btn-outline-primary w-100 mb-2"
							onClick={() => checkSocket('bot')}>
							{t('admin_tools_check_bot')}
						</button>
						<button type="button" className="btn btn-outline-primary w-100"
							onClick={() => checkSocket('updates')}>
							{t('admin_tools_check_updates')}
						</button>
					</div>
				</div>

				<div className="card mt-3">
					<div className="card-body">
						<div className="tool-section-title">
							Обновить одну кассу
						</div>
						<div className="text-muted fz-12 mb-3">
							Команда уходит только на выбранную точку. Касса ставит версию из облака, если она новее. Работает на кассах, которые уже знают эту команду.
						</div>

						<div className="form-group">
							<label>Логин</label>
							<Select
								options={users}
								value={updateUser}
								onChange={(option) => handleSelectUpdateUser(option)}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => `${option.login} — ${option.name || ''}`}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group">
							<label>Торговая точка</label>
							<Select
								options={updatePoses}
								value={updatePos}
								onChange={(option) => setUpdatePos(option)}
								isDisabled={!updateUser}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => `${option.name} (#${option.id})`}
								getOptionValue={(option) => option.id}
							/>
						</div>

						<button type="button" className="btn btn-primary w-100 mt-2"
							disabled={!updatePos}
							onClick={() => sendPosUpdate()}>
							<i className="uil uil-sync me-1"></i>
							Обновить кассу
						</button>
					</div>
				</div>
			</div>

			<div className="col-md-7">
				<div className="card">
					<div className="card-body">
						<div className="tool-section-title">
							{t('admin_tools_product_wms')}
						</div>
						<div className="text-muted fz-12 mb-3">
							{t('admin_tools_product_wms_hint')}
						</div>

						<div className="row">
							<div className="form-group col-md-6">
								<label>{t('admin_tools_products_login')}</label>
								<Select
									options={users}
									value={fromUser}
									onChange={(option) => setFromUser(option)}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => `${option.login} — ${option.name || ''}`}
									getOptionValue={(option) => option.id}
								/>
							</div>
							<div className="form-group col-md-6">
								<label>{t('admin_tools_wms_login')}</label>
								<Select
									options={users}
									value={toUser}
									onChange={(option) => handleSelectToUser(option)}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => `${option.login} — ${option.name || ''}`}
									getOptionValue={(option) => option.id}
								/>
							</div>
							<div className="form-group col-md-6">
								<label>{t('pos')}</label>
								<Select
									options={poses}
									value={selectedPos}
									onChange={(option) => handleSelectPos(option)}
									isDisabled={!toUser}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
							<div className="form-group col-md-6">
								<label>{t('wms')}</label>
								<Select
									options={warehouses}
									value={selectedWms}
									onChange={(option) => setSelectedWms(option)}
									isDisabled={!selectedPos}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
						</div>

						<button type="button" className="btn btn-primary w-100 mt-2"
							onClick={() => linkProductWms()}>
							{t('admin_tools_link')}
						</button>

						{result &&
							<div className="tool-result mt-3">
								<div className="tool-result-row">
									<span>{t('products')}</span>
									<b>{result.products_total ?? 0}</b>
								</div>
								<div className="tool-result-row">
									<span>{t('admin_tools_products_linked')}</span>
									<b className="text-success">{result.products_linked ?? 0}</b>
								</div>
							</div>
						}
					</div>
				</div>
			</div>
		</div>
	)
}

export default ServiceTab
